export const PORTFOLIO_BENCHMARK_SERIES_IDS = [
  "SPY_TOTAL_RETURN",
  "QQQ_TOTAL_RETURN",
  "IWM_TOTAL_RETURN",
  "CASH_3M_TBILL",
] as const;

export type PortfolioBenchmarkSeriesId = typeof PORTFOLIO_BENCHMARK_SERIES_IDS[number];

export type PortfolioBenchmarkPolicy = {
  schemaVersion: "tradvera.portfolio-benchmark-policy.v1";
  primaryBenchmark: "SPY_TOTAL_RETURN";
  diagnosticBenchmarks: Array<"QQQ_TOTAL_RETURN" | "IWM_TOTAL_RETURN">;
  cashBenchmark: "CASH_3M_TBILL";
  snapshotCadence: "daily_eod";
  returnBasis: "total_return";
  portfolioMeasurement: "time_weighted_return";
  ownerOutcomeMeasurement: "money_weighted_return_xirr";
  costBasis: "net_of_trading_and_allocated_operating_costs";
  benchmarkSelection: "locked_before_measurement_period";
  appendOnly: true;
};

export type BenchmarkObservation = {
  schemaVersion: "tradvera.benchmark-observation.v1";
  id: string;
  portfolioId: string;
  environment: "paper" | "real";
  seriesId: PortfolioBenchmarkSeriesId;
  tradingDate: string;
  totalReturnLevel: number;
  priceClose?: number;
  currency: "USD";
  sourceId: string;
  sourceRecordId: string;
  asOf: string;
  knownAt: string;
  fingerprintSha256: string;
  appendOnly: true;
};

export type DailyValuationInput = {
  tradingDate: string;
  openingValue: number;
  closingValue: number;
  netExternalFlow: number;
  externalFlowTiming: "start" | "end" | "none";
};

export type DailyReturnPoint = {
  tradingDate: string;
  returnDecimal: number;
};

export type PerformanceComparison = {
  schemaVersion: "tradvera.portfolio-performance-comparison.v1";
  status: "abstain" | "preview" | "measured";
  reasons: string[];
  alignedTradingDays: number;
  fromDate: string | null;
  toDate: string | null;
  portfolioCumulativeReturn: number | null;
  benchmarkCumulativeReturn: number | null;
  activeCumulativeReturn: number | null;
  portfolioAnnualizedReturn: number | null;
  benchmarkAnnualizedReturn: number | null;
  annualizedTrackingError: number | null;
  informationRatio: number | null;
  beta: number | null;
  correlation: number | null;
  portfolioMaxDrawdown: number | null;
  benchmarkMaxDrawdown: number | null;
};

export type MoneyWeightedCashFlow = {
  date: string;
  amount: number;
};

export function defaultPortfolioBenchmarkPolicy(): PortfolioBenchmarkPolicy {
  return {
    schemaVersion: "tradvera.portfolio-benchmark-policy.v1",
    primaryBenchmark: "SPY_TOTAL_RETURN",
    diagnosticBenchmarks: ["QQQ_TOTAL_RETURN", "IWM_TOTAL_RETURN"],
    cashBenchmark: "CASH_3M_TBILL",
    snapshotCadence: "daily_eod",
    returnBasis: "total_return",
    portfolioMeasurement: "time_weighted_return",
    ownerOutcomeMeasurement: "money_weighted_return_xirr",
    costBasis: "net_of_trading_and_allocated_operating_costs",
    benchmarkSelection: "locked_before_measurement_period",
    appendOnly: true,
  };
}

export function normalizePortfolioBenchmarkPolicy(value?: Partial<PortfolioBenchmarkPolicy> | null): PortfolioBenchmarkPolicy {
  const fallback = defaultPortfolioBenchmarkPolicy();
  const diagnostics = Array.isArray(value?.diagnosticBenchmarks)
    ? value.diagnosticBenchmarks.filter((item): item is "QQQ_TOTAL_RETURN" | "IWM_TOTAL_RETURN" => item === "QQQ_TOTAL_RETURN" || item === "IWM_TOTAL_RETURN")
    : fallback.diagnosticBenchmarks;
  return {
    ...fallback,
    diagnosticBenchmarks: [...new Set(diagnostics)],
  };
}

export function calculateDailyPortfolioReturn(input: DailyValuationInput): DailyReturnPoint | null {
  if (![input.openingValue, input.closingValue, input.netExternalFlow].every(Number.isFinite)) return null;
  if (input.openingValue <= 0 || input.closingValue < 0) return null;
  let denominator = input.openingValue;
  let numerator = input.closingValue;
  if (input.externalFlowTiming === "start") denominator += input.netExternalFlow;
  if (input.externalFlowTiming === "end") numerator -= input.netExternalFlow;
  if (input.externalFlowTiming === "none" && input.netExternalFlow !== 0) return null;
  if (denominator <= 0) return null;
  const returnDecimal = numerator / denominator - 1;
  if (!Number.isFinite(returnDecimal) || returnDecimal <= -1) return null;
  return { tradingDate: input.tradingDate, returnDecimal };
}

export function totalReturnLevelsToDailyReturns(observations: Array<Pick<BenchmarkObservation, "tradingDate" | "totalReturnLevel">>): DailyReturnPoint[] {
  const sorted = [...observations]
    .filter((item) => Number.isFinite(item.totalReturnLevel) && item.totalReturnLevel > 0)
    .sort((a, b) => a.tradingDate.localeCompare(b.tradingDate));
  const returns: DailyReturnPoint[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index].tradingDate === sorted[index - 1].tradingDate) continue;
    returns.push({
      tradingDate: sorted[index].tradingDate,
      returnDecimal: sorted[index].totalReturnLevel / sorted[index - 1].totalReturnLevel - 1,
    });
  }
  return returns;
}

export function chainLinkedReturn(points: Array<Pick<DailyReturnPoint, "returnDecimal">>): number | null {
  if (!points.length || points.some((point) => !Number.isFinite(point.returnDecimal) || point.returnDecimal <= -1)) return null;
  return points.reduce((wealth, point) => wealth * (1 + point.returnDecimal), 1) - 1;
}

export function comparePortfolioWithBenchmark(portfolio: DailyReturnPoint[], benchmark: DailyReturnPoint[]): PerformanceComparison {
  const benchmarkByDate = new Map(benchmark.map((point) => [point.tradingDate, point.returnDecimal]));
  const aligned = [...portfolio]
    .filter((point) => benchmarkByDate.has(point.tradingDate))
    .sort((a, b) => a.tradingDate.localeCompare(b.tradingDate))
    .map((point) => ({ date: point.tradingDate, portfolio: point.returnDecimal, benchmark: benchmarkByDate.get(point.tradingDate)! }))
    .filter((point) => [point.portfolio, point.benchmark].every(Number.isFinite) && point.portfolio > -1 && point.benchmark > -1);
  if (aligned.length < 2) return emptyComparison("INSUFFICIENT_ALIGNED_DAILY_OBSERVATIONS", aligned.length);
  const p = aligned.map((point) => point.portfolio);
  const b = aligned.map((point) => point.benchmark);
  const active = p.map((value, index) => value - b[index]);
  const portfolioCumulativeReturn = productReturn(p);
  const benchmarkCumulativeReturn = productReturn(b);
  const annualizedTrackingError = sampleStdDev(active) * Math.sqrt(252);
  const annualizedActiveReturn = mean(active) * 252;
  const covariance = sampleCovariance(p, b);
  const benchmarkVariance = sampleVariance(b);
  return {
    schemaVersion: "tradvera.portfolio-performance-comparison.v1",
    status: aligned.length >= 20 ? "measured" : "preview",
    reasons: aligned.length >= 20 ? [] : ["PREVIEW_REQUIRES_20_ALIGNED_TRADING_DAYS"],
    alignedTradingDays: aligned.length,
    fromDate: aligned[0].date,
    toDate: aligned.at(-1)!.date,
    portfolioCumulativeReturn,
    benchmarkCumulativeReturn,
    activeCumulativeReturn: portfolioCumulativeReturn - benchmarkCumulativeReturn,
    portfolioAnnualizedReturn: annualize(portfolioCumulativeReturn, aligned.length),
    benchmarkAnnualizedReturn: annualize(benchmarkCumulativeReturn, aligned.length),
    annualizedTrackingError,
    informationRatio: annualizedTrackingError > 0 ? annualizedActiveReturn / annualizedTrackingError : null,
    beta: benchmarkVariance > 0 ? covariance / benchmarkVariance : null,
    correlation: correlation(p, b),
    portfolioMaxDrawdown: maxDrawdown(p),
    benchmarkMaxDrawdown: maxDrawdown(b),
  };
}

export function calculateXirr(cashFlows: MoneyWeightedCashFlow[]): number | null {
  const normalized = cashFlows
    .filter((flow) => Number.isFinite(flow.amount) && Number.isFinite(Date.parse(flow.date)))
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  if (normalized.length < 2 || !normalized.some((flow) => flow.amount < 0) || !normalized.some((flow) => flow.amount > 0)) return null;
  const start = Date.parse(normalized[0].date);
  const npv = (rate: number) => normalized.reduce((sum, flow) => {
    const years = (Date.parse(flow.date) - start) / (365.2425 * 24 * 60 * 60 * 1_000);
    return sum + flow.amount / ((1 + rate) ** years);
  }, 0);
  let low = -0.9999;
  let high = 1;
  let lowValue = npv(low);
  let highValue = npv(high);
  while (Math.sign(lowValue) === Math.sign(highValue) && high < 1_000_000) {
    high *= 2;
    highValue = npv(high);
  }
  if (!Number.isFinite(lowValue) || !Number.isFinite(highValue) || Math.sign(lowValue) === Math.sign(highValue)) return null;
  for (let iteration = 0; iteration < 200; iteration += 1) {
    const middle = (low + high) / 2;
    const middleValue = npv(middle);
    if (Math.abs(middleValue) < 1e-10) return middle;
    if (Math.sign(middleValue) === Math.sign(lowValue)) {
      low = middle;
      lowValue = middleValue;
    } else {
      high = middle;
      highValue = middleValue;
    }
  }
  return (low + high) / 2;
}

function emptyComparison(reason: string, alignedTradingDays: number): PerformanceComparison {
  return {
    schemaVersion: "tradvera.portfolio-performance-comparison.v1",
    status: "abstain",
    reasons: [reason],
    alignedTradingDays,
    fromDate: null,
    toDate: null,
    portfolioCumulativeReturn: null,
    benchmarkCumulativeReturn: null,
    activeCumulativeReturn: null,
    portfolioAnnualizedReturn: null,
    benchmarkAnnualizedReturn: null,
    annualizedTrackingError: null,
    informationRatio: null,
    beta: null,
    correlation: null,
    portfolioMaxDrawdown: null,
    benchmarkMaxDrawdown: null,
  };
}

function productReturn(values: number[]) { return values.reduce((wealth, value) => wealth * (1 + value), 1) - 1; }
function annualize(cumulative: number, periods: number) { return (1 + cumulative) ** (252 / periods) - 1; }
function mean(values: number[]) { return values.reduce((sum, value) => sum + value, 0) / values.length; }
function sampleVariance(values: number[]) {
  if (values.length < 2) return 0;
  const average = mean(values);
  return values.reduce((sum, value) => sum + (value - average) ** 2, 0) / (values.length - 1);
}
function sampleStdDev(values: number[]) { return Math.sqrt(sampleVariance(values)); }
function sampleCovariance(left: number[], right: number[]) {
  if (left.length !== right.length || left.length < 2) return 0;
  const leftMean = mean(left);
  const rightMean = mean(right);
  return left.reduce((sum, value, index) => sum + (value - leftMean) * (right[index] - rightMean), 0) / (left.length - 1);
}
function correlation(left: number[], right: number[]) {
  const denominator = sampleStdDev(left) * sampleStdDev(right);
  return denominator > 0 ? sampleCovariance(left, right) / denominator : null;
}
function maxDrawdown(returns: number[]) {
  let wealth = 1;
  let peak = 1;
  let drawdown = 0;
  for (const value of returns) {
    wealth *= 1 + value;
    peak = Math.max(peak, wealth);
    drawdown = Math.min(drawdown, wealth / peak - 1);
  }
  return drawdown;
}
